/**
 * Compact sidebar card showing a member's most recent checkout invoices.
 * Clicking "View all" opens the full invoice history dialog.
 */
import { ChevronRight, FileText, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { formatClinicPrice } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import type { InvoiceGroup } from '@/frontend/features/billing/invoices.utils'
import { useListMemberInvoices } from '@/frontend/features/members/hooks/use-list-member-invoices'
import { MemberInvoicesDialog } from '@/frontend/features/members/member-invoices-dialog'

/** Maximum number of invoices shown in the sidebar preview. */
const PREVIEW_COUNT = 5

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface MemberInvoicesCardProps {
  memberId: string
}

/** Sidebar card previewing recent invoices with a "View all" expansion. */
export function MemberInvoicesCard({ memberId }: MemberInvoicesCardProps) {
  const { data: invoices, isLoading } = useListMemberInvoices(memberId)
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <>
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setDialogOpen(true)}>
          <CardTitle>Invoices</CardTitle>
          <CardAction>
            <ChevronRight className="size-4 text-muted-foreground" />
          </CardAction>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full rounded" />
              <Skeleton className="h-8 w-full rounded" />
              <Skeleton className="h-8 w-full rounded" />
            </div>
          ) : !invoices || invoices.length === 0 ? (
            <p className="text-muted-foreground">No invoices yet</p>
          ) : (
            <div className="space-y-2">
              {invoices.slice(0, PREVIEW_COUNT).map((group) => (
                <InvoicePreviewRow key={group.paymentId} group={group} />
              ))}

              {invoices.length > PREVIEW_COUNT && (
                <p className="text-sm text-muted-foreground">
                  +{invoices.length - PREVIEW_COUNT} more
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <MemberInvoicesDialog
        invoices={invoices ?? []}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

/** Compact row showing date, order count, and total for an invoice group. */
function InvoicePreviewRow({ group }: { group: InvoiceGroup }) {
  const allRefills = group.orders.every((o) => o.isRefill)

  return (
    <div className="flex items-center gap-2">
      {allRefills ? (
        <RefreshCw className="size-3.5 shrink-0 text-info-fg" />
      ) : (
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="flex-1 truncate text-muted-foreground">
        <FormattedDate value={group.date} clinicId={group.orders[0]?.clinicId} />
      </span>
      <span className="tabular-nums text-muted-foreground">
        {group.orders.length} {group.orders.length === 1 ? 'item' : 'items'}
      </span>
      <span className="tabular-nums font-medium">
        {formatClinicPrice(group.total, group.orders[0].clinicId)}
      </span>
    </div>
  )
}
